import {
  DefenseIcon,
  SectorMapIcon,
  TalentPath,
  TechNode,
  TradeIcon,
  ViewHeader,
} from '@spacegame/ui';
import { RESEARCH_KEYS, TALENT_PATHS, TALENT_MAX_PER_PATH, type ResearchKey } from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { researchNode } from '../game/selectors';
import { entityArt } from '../components/EntityArt';

const PATH_ICONS = {
  economy: <TradeIcon size={18} />,
  war: <DefenseIcon size={18} />,
  astronomy: <SectorMapIcon size={18} />,
} as const;

const RESEARCH_SPRITE: Record<ResearchKey, string> = {
  impulseDrive: 'tech-impulse',
  warpFold: 'tech-warp',
  shieldHarmonics: 'tech-shield',
  asteroidMining: 'tech-mining',
  logistics: 'tech-logistics',
  astrometrics: 'tech-astrometrics',
  gravitonPhysics: 'tech-graviton',
};

export function ResearchView() {
  const { game } = useGame();
  const actions = useGameActions();
  const lab = game.planets[0]!.buildings.researchLab;

  return (
    <>
      <ViewHeader
        eyebrow={lab > 0 ? `Quanten-Forschungslabor Stufe ${lab}` : 'Kein Forschungslabor'}
        title="Forschungsbaum"
        description={
          lab > 0
            ? 'Forschungsschritte schalten schiffstechnische Verbesserungen frei und optimieren die Infrastruktur.'
            : 'Errichte zuerst ein Quanten-Forschungslabor, um Technologien zu erforschen.'
        }
      />
      <div className="space-y-2.5">
        {RESEARCH_KEYS.map((key) => {
          const node = researchNode(game, key);
          return (
            <TechNode
              key={key}
              name={node.label}
              icon={entityArt(RESEARCH_SPRITE[key], node.label)}
              effect={node.effect}
              level={node.level}
              maxLevel={node.maxLevel}
              state={node.state}
              cost={node.cost}
              available={game.resources}
              duration={node.durationSeconds}
              finishesAt={node.finishesAt}
              requirement={node.requirement}
              onResearch={() => actions.startResearch(key)}
            />
          );
        })}
      </div>

      <ViewHeader
        eyebrow={`${game.talentPoints} Punkt${game.talentPoints === 1 ? '' : 'e'} verfügbar`}
        title="Talentbaum"
      />
      <div className="grid gap-3 lg:grid-cols-3">
        {(Object.keys(TALENT_PATHS) as Array<keyof typeof TALENT_PATHS>).map((path) => {
          const def = TALENT_PATHS[path];
          return (
            <TalentPath
              key={path}
              path={path}
              name={def.label}
              icon={PATH_ICONS[path]}
              invested={game.talents[path]}
              max={TALENT_MAX_PER_PATH}
              tiers={def.tiers.map((tier) => ({ range: [tier.from, tier.to], effect: tier.effect }))}
              availablePoints={game.talentPoints}
              onInvest={() => actions.spendTalentPoint(path)}
            />
          );
        })}
      </div>
    </>
  );
}
